import {
  updateExistingModelSuccess,
  updateExistingModelError,
  updateExistingModelBusy,
  duplicateModelSuccess,
  duplicateModelError,
  duplicateModelBusy,
  deleteExistingModelSuccess,
  deleteExistingModelError,
  deleteExistingModelBusy,
  readAllModelsSuccess,
  readAllModelsError,
  readAllModelsBusy,
  readExistingModelSuccess,
  readExistingModelError,
  readExistingModelBusy,
  runExistingModelSuccess,
  runExistingModelError,
  runExistingModelBusy,
  stopModelSuccess,
  stopModelError,
  stopModelBusy,
  getModelProgressSuccess,
  getModelProgressError,
  getModelProgressBusy,
  persistModelDiffState,
  UPDATE_MODEL,
  DUPLICATE_MODEL,
  DELETE_MODEL,
  READ_MODELS,
  READ_A_MODEL,
  RUN_MODEL,
  STOP_MODEL,
  PROGRESS,
  selectReadAllModels,
  catalogModels as catalogModelsAction,
  catalogModelsBusy,
  catalogModelsError,
  catalogModelsSuccess,
  CATALOG_MODELS,
  selectCatalogModelsRequest,
  selectCatalogModels,
  APPLY_AI_MODEL_INPUT_CHANGE,
  setStoredModelInput,
  setUnsavedModelInput,
  setAiChangedInputPaths,
  selectStoredModelInputs,
  selectUnsavedModelInputs,
  selectAiChangedInputPaths,
  selectReadExistingModel,
  shareModelSuccess,
  shareModelError,
  shareModelBusy,
  SHARE_MODEL,
  readModelSharesSuccess,
  readModelSharesError,
  readModelSharesBusy,
  READ_MODEL_SHARES,
  revokeModelShareSuccess,
  revokeModelShareError,
  revokeModelShareBusy,
  REVOKE_MODEL_SHARE,
} from "./action";
import { call, delay, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import {
  updateModel,
  deleteModel,
  readModels,
  readModel,
  runModel,
  cancelModel,
  getModelProgress,
  shareModel as shareModelApi,
  readModelShares as readModelSharesApi,
  revokeModelShare as revokeModelShareApi,
} from "./api";
import { ActionTypes } from "../util";
import { logError } from "utils/utils";
import moment from "moment";
import { filter } from "utils/async";
import { FETCH_SERVICES } from "controllers/services/action";
import { clearCachedModelSummariesForModel } from "ai-assistant/services/model-summary-cache";
import { ModelSummaryService } from "ai-assistant/services/model-summary-service";
import { createModel } from "controllers/services/api";
import { camelCase, cloneDeep, has, isArray, merge, omit, set as setValueOnPath, upperFirst } from "lodash";
import { selectUser } from "controllers/user/action";
import { setMessage, setCurrentModel, selectCurrentModel, selectDefaultModels } from "controllers/common/action";
import { Intent } from "@blueprintjs/core";

const { REQUEST, SUCCESS } = ActionTypes;
const modelSummaryService = new ModelSummaryService();
export function transformModelPayload(payload) {
  let oldBody = payload;
  let message = "";
  if (oldBody.message) {
    message = oldBody.message;
    oldBody = omit(oldBody, "message");
  }
  const body = { ...oldBody.input, ...omit(oldBody, "input") };
  return { body, message };
}
export function* updateModelSaga(action) {
  const { uuid } = action.payload;
  const { body, message } = transformModelPayload(action.payload);
  try {
    const storedModelInputs = yield select(selectStoredModelInputs);
    const aiChangedInputPaths = yield select(selectAiChangedInputPaths);
    yield put(persistModelDiffState({ storedModelInputs, aiChangedInputPaths }));
    yield put(updateExistingModelBusy(true));
    yield put(updateExistingModelError());
    const response = yield call(updateModel, body);
    yield put(updateExistingModelSuccess(merge({ uuid }, { response })));
    yield put(setMessage({ text: message, intent: Intent.SUCCESS }));
    yield call(readModelsSaga);
  } catch (error) {
    logError(error);
    yield put(updateExistingModelError(merge({ uuid }, { error: error.message })));
  } finally {
    yield put(updateExistingModelBusy(false));
  }
}

export function* duplicateModelSaga(action) {
  const { uuid } = action.payload;
  const { body, message } = transformModelPayload(action.payload);
  try {
    yield put(duplicateModelBusy(true));
    yield put(duplicateModelError());
    const existModel = yield call(readModel, {
      modelName: body.existingModelName,
      owner: body.owner,
      isShared: body.isShared,
    });
    // Drop source ownership/share metadata so the server assigns the recipient as owner.
    const sourceInput = omit(existModel.input || {}, [
      "owner",
      "user",
      "isShared",
      "sharedRole",
      "ownerName",
      "modelName",
      "created",
      "editDate",
    ]);
    const response = yield call(createModel, {
      ...sourceInput,
      output: existModel.output,
      modelName: body.modelName,
      modelType: body.modelType,
    });
    yield put(duplicateModelSuccess(merge({ uuid }, { response })));
    yield put(setMessage({ text: message, intent: Intent.SUCCESS }));
    yield call(readModelsSaga);
  } catch (error) {
    logError(error);
    yield put(duplicateModelError(merge({ uuid }, { error: error.message })));
  } finally {
    yield put(duplicateModelBusy(false));
  }
}

export function* deleteModelSaga(action) {
  const { body, message } = transformModelPayload(action.payload);
  try {
    yield put(deleteExistingModelBusy(true));
    yield put(deleteExistingModelError());
    if (body.modelName) {
      yield call([modelSummaryService, modelSummaryService.deleteSummaryCache], body.modelName);
    }
    const response = yield call(deleteModel, body);
    if (body.modelName) {
      yield call(clearCachedModelSummariesForModel, body.modelName);
    }
    yield put(deleteExistingModelSuccess(response));
    yield call(readModelsSaga);
    yield put(setMessage({ text: message, intent: Intent.SUCCESS }));
  } catch (error) {
    logError(error);
    yield put(deleteExistingModelError(error.message));
  } finally {
    yield put(deleteExistingModelBusy(false));
  }
}

const transformStatus = (model) => {
  const transformedModel = model;
  switch (model.status) {
    case "finished":
      transformedModel.status = "Completed";
      break;
    case "stopped":
      transformedModel.status = "Drafted";
      break;
    case "error":
      transformedModel.status = "Failed";
      break;
    default:
      transformedModel.status = upperFirst(camelCase(model.status));
  }
  return transformedModel;
};

const transformModels = (models) => {
  if (has(models, ["models"]) && isArray(models.models)) {
    models = models.models;
  }
  return models.map((model) => transformStatus(model));
};

export function* readModelsSaga() {
  try {
    const user = yield select(selectUser);
    if (process.env.NODE_ENV !== "test" && !user) {
      return;
    }
    yield put(readAllModelsBusy(true));
    yield put(readAllModelsError());
    let response = yield call(readModels);
    response = yield call(transformModels, response);
    yield put(readAllModelsSuccess(response));
    const request = yield select(selectCatalogModelsRequest);
    yield call(
      catalogModelsSaga,
      catalogModelsAction(merge({ auto: true, field: "editDate", direction: "desc" }, request))
    );
  } catch (error) {
    logError(error);
    yield put(readAllModelsError(error.message));
  } finally {
    yield put(readAllModelsBusy(false));
  }
}

const catalogModels = (models, field, direction, search) => {
  if (!Array.isArray(models)) {
    return models;
  }
  let result = models.slice();
  if (field && direction) {
    let comparator;
    switch (direction) {
      case "asc":
        switch (field) {
          case "created":
          case "editDate":
            comparator = (a, b) => moment(a[field]).valueOf() - moment(b[field]).valueOf();
            break;
          default:
            comparator = (a, b) => a[field] && a[field].localeCompare(b[field]);
        }
        break;
      default:
        switch (field) {
          case "created":
          case "editDate":
            comparator = (a, b) => moment(b[field]).valueOf() - moment(a[field]).valueOf();
            break;
          default:
            comparator = (a, b) => b[field] && b[field].localeCompare(a[field]);
        }
        break;
    }
    result = result.sort(comparator);
  }
  return filter(result, search, ["modelName", "status", "modelType", "ownerName"]);
};

export function* catalogModelsSaga(action) {
  const { field, direction, search, auto } = action.payload;
  try {
    yield delay(parseInt(process.env.REACT_APP_DEBOUNCE));
    yield put(catalogModelsBusy(auto ? "auto" : "user"));
    yield put(catalogModelsError());
    let response = yield select(selectReadAllModels);
    response = yield call(catalogModels, response, field, direction, search);
    yield put(catalogModelsSuccess(response));
  } catch (error) {
    logError(error);
    yield put(catalogModelsError(error.message));
  } finally {
    yield put(catalogModelsBusy(false));
  }
}

export function* readModelSaga(action) {
  const body = action.payload;
  try {
    yield put(readExistingModelBusy(true));
    yield put(readExistingModelError());
    const response = yield call(readModel, body);
    yield put(readExistingModelSuccess(response));
  } catch (error) {
    logError(error);
    yield put(readExistingModelError(error.message));
  } finally {
    yield put(readExistingModelBusy(false));
  }
}

export function* applyAiModelInputChangeSaga(action) {
  const payload = action.payload || {};
  const path = payload.path || payload.input_path || payload.inputPath;
  if (!path) {
    return;
  }

  const currentModel = yield select(selectCurrentModel);
  if (!currentModel) {
    return;
  }

  const modelName = payload.modelName || payload.model_name;
  const draftId = payload.draftId || payload.draft_id;
  const modelType = payload.modelType || payload.model_type;

  const isSavedMatch = Boolean(modelName && currentModel.modelName === modelName);
  const isDraftMatch = Boolean(
    draftId &&
      currentModel.draftId &&
      currentModel.draftId === draftId &&
      (!currentModel.modelName || !modelName || currentModel.modelName === modelName) &&
      (!modelType || !currentModel.modelType || currentModel.modelType === modelType)
  );

  if (!isSavedMatch && !isDraftMatch) {
    return;
  }

  const storageKey = modelName || draftId;
  if (!storageKey) {
    return;
  }

  const storedModelInputs = yield select(selectStoredModelInputs);
  const hasStoredState =
    storedModelInputs && Object.prototype.hasOwnProperty.call(storedModelInputs, storageKey);

  const unsavedModelInputs = yield select(selectUnsavedModelInputs);
  const aiChangedPaths = yield select(selectAiChangedInputPaths);

  let baseInput;
  let existingModel;

  if (isSavedMatch) {
    existingModel = yield select(selectReadExistingModel);
    baseInput = existingModel?.input;
  } else {
    baseInput = unsavedModelInputs?.[storageKey];
    if (!baseInput) {
      const defaultModels = yield select(selectDefaultModels);
      baseInput = defaultModels ? defaultModels[currentModel.modelType] : undefined;
    }
    if (!baseInput && currentModel.input) {
      baseInput = currentModel.input;
    }
  }

  if (!baseInput) {
    return;
  }

  if (!hasStoredState) {
    const nextStoredInputs = {
      ...(storedModelInputs || {}),
      [storageKey]: cloneDeep(baseInput),
    };
    yield put(setStoredModelInput(nextStoredInputs));
  }

  const nextInput = cloneDeep(baseInput);
  const nextValue = payload.value !== undefined ? payload.value : payload.new_value ?? payload.newValue;
  setValueOnPath(nextInput, path, cloneDeep(nextValue));

  const nextAiChangedPaths = { ...(aiChangedPaths || {}) };
  const currentPaths = new Set(nextAiChangedPaths[storageKey] || []);
  currentPaths.add(path);
  nextAiChangedPaths[storageKey] = Array.from(currentPaths);
  yield put(setAiChangedInputPaths(nextAiChangedPaths));

  if (isSavedMatch && existingModel) {
    const updatedModel = { ...existingModel, input: nextInput };
    yield put(readExistingModelSuccess(updatedModel));
  } else if (isDraftMatch) {
    const nextUnsavedInputs = {
      ...(unsavedModelInputs || {}),
      [storageKey]: nextInput,
    };
    yield put(setUnsavedModelInput(nextUnsavedInputs));
  }
}

export function* runModelSaga(action) {
  const body = action.payload;
  try {
    const storedModelInputs = yield select(selectStoredModelInputs);
    const aiChangedInputPaths = yield select(selectAiChangedInputPaths);
    yield put(persistModelDiffState({ storedModelInputs, aiChangedInputPaths }));
    yield put(runExistingModelBusy(true));
    yield put(runExistingModelError());
    const response = yield call(runModel, body);
    yield put(runExistingModelSuccess(response ? response : "Success"));
  } catch (error) {
    logError(error);
    yield put(runExistingModelError(error.message));
  } finally {
    yield put(runExistingModelBusy(false));
  }
}

export function* stopModelSaga(action) {
  const body = action.payload;
  try {
    yield put(stopModelBusy(true));
    yield put(stopModelError());
    const response = yield call(cancelModel, body);
    yield put(stopModelSuccess(response));
  } catch (error) {
    logError(error);
    yield put(stopModelError(error.message));
  } finally {
    yield put(stopModelBusy(false));
  }
}

export function* getModelProgressSaga(action) {
  const body = action.payload;
  try {
    yield put(getModelProgressBusy(true));
    yield put(getModelProgressError());
    const response = yield call(getModelProgress, body);
    yield put(getModelProgressSuccess(response));
  } catch (error) {
    logError(error);
    yield put(getModelProgressError(error.message));
  } finally {
    yield put(getModelProgressBusy(false));
  }
}

export function* shareModelSaga(action) {
  const { modelName, email, message, pendingMessage } = action.payload;
  try {
    yield put(shareModelBusy(true));
    yield put(shareModelError());
    const response = yield call(shareModelApi, { modelName, email });
    yield put(shareModelSuccess(response));
    const text =
      response?.status === "pending"
        ? pendingMessage || "Invitation sent"
        : message || "Model shared";
    yield put(setMessage({ text, intent: Intent.SUCCESS }));
    yield call(readModelSharesSaga, { payload: { modelName } });
    yield call(readModelsSaga);
    yield call(syncCurrentModelShareFlags, modelName);
  } catch (error) {
    logError(error);
    yield put(shareModelError(error.message));
    yield put(setMessage({ text: error.message, intent: Intent.DANGER }));
  } finally {
    yield put(shareModelBusy(false));
  }
}

function* syncCurrentModelShareFlags(modelName) {
  const current = yield select(selectCurrentModel);
  if (!current?.modelName || current.modelName !== modelName || current.isShared) {
    return;
  }
  const catalog = yield select(selectCatalogModels);
  const updated = catalog?.find((m) => m.modelName === modelName && !m.isShared);
  if (updated) {
    yield put(
      setCurrentModel({
        ...current,
        sharedWithOthers: updated.sharedWithOthers,
        shareCount: updated.shareCount,
      })
    );
  }
}

export function* readModelSharesSaga(action) {
  const { modelName } = action.payload;
  try {
    yield put(readModelSharesBusy(true));
    yield put(readModelSharesError());
    const response = yield call(readModelSharesApi, { modelName });
    yield put(readModelSharesSuccess(response));
  } catch (error) {
    logError(error);
    yield put(readModelSharesError(error.message));
  } finally {
    yield put(readModelSharesBusy(false));
  }
}

export function* revokeModelShareSaga(action) {
  const { modelName, shareTarget, message } = action.payload;
  try {
    yield put(revokeModelShareBusy(true));
    yield put(revokeModelShareError());
    yield call(revokeModelShareApi, { modelName, shareTarget });
    yield put(revokeModelShareSuccess({ modelName, shareTarget }));
    yield put(setMessage({ text: message || "Share revoked", intent: Intent.SUCCESS }));
    yield call(readModelSharesSaga, { payload: { modelName } });
    yield call(readModelsSaga);
    yield call(syncCurrentModelShareFlags, modelName);
  } catch (error) {
    logError(error);
    yield put(revokeModelShareError(error.message));
    yield put(setMessage({ text: error.message, intent: Intent.DANGER }));
  } finally {
    yield put(revokeModelShareBusy(false));
  }
}

export default function* modelsSaga() {
  yield takeLatest(UPDATE_MODEL[REQUEST], updateModelSaga);
  yield takeLatest(DUPLICATE_MODEL[REQUEST], duplicateModelSaga);
  yield takeEvery(DELETE_MODEL[REQUEST], deleteModelSaga);
  yield takeLatest(READ_MODELS[REQUEST], readModelsSaga);
  yield takeLatest(FETCH_SERVICES[SUCCESS], readModelsSaga);
  yield takeLatest(CATALOG_MODELS[REQUEST], catalogModelsSaga);
  yield takeLatest(READ_A_MODEL[REQUEST], readModelSaga);
  yield takeEvery(APPLY_AI_MODEL_INPUT_CHANGE, applyAiModelInputChangeSaga);
  yield takeLatest(RUN_MODEL[REQUEST], runModelSaga);
  yield takeLatest(STOP_MODEL[REQUEST], stopModelSaga);
  yield takeLatest(PROGRESS[REQUEST], getModelProgressSaga);
  yield takeLatest(SHARE_MODEL[REQUEST], shareModelSaga);
  yield takeLatest(READ_MODEL_SHARES[REQUEST], readModelSharesSaga);
  yield takeLatest(REVOKE_MODEL_SHARE[REQUEST], revokeModelShareSaga);
}
